/**
 * SH Minimum Purchase Quantity Handler
 * Enforces minimum purchase quantity rules on PDP, Cart, and Product Sets
 * SH Updated: January 2026 - Initial implementation
 * SH Updated: January 2026 - Added threshold mode support
 * SH Updated: January 2026 - Added variant-level min qty support
 *
 * Two modes:
 * - Increment mode (data-min-qty): min 4 means 4, 8, 12, 16...
 * - Threshold mode (data-min-threshold): min 4 means 4, 5, 6, 7...
 * If both are set, increment mode takes priority.
 *
 * Priority: variant metafield > product metafield > default (1)
 */
(function () {
  'use strict';

  /**
   * Get quantity config from element
   * Returns { minQty, increment } where:
   * - minQty: the minimum allowed quantity
   * - increment: the step value (equals minQty for increment mode, 1 for threshold mode)
   */
  function getQuantityConfig(element) {
    const minQty = parseInt(element.dataset.minQty) || 0;
    const minThreshold = parseInt(element.dataset.minThreshold) || 0;

    // Increment mode takes priority
    if (minQty > 1) {
      return { minQty: minQty, increment: minQty, mode: 'increment' };
    }

    // Threshold mode
    if (minThreshold > 1) {
      return { minQty: minThreshold, increment: 1, mode: 'threshold' };
    }

    // Default: no restrictions
    return { minQty: 1, increment: 1, mode: 'default' };
  }

  /**
   * Get valid quantity based on mode
   * - Increment mode: round up to nearest multiple of minQty
   * - Threshold mode: ensure at least minQty, increment by 1
   */
  function getValidQuantity(qty, config) {
    if (config.mode === 'increment') {
      return Math.ceil(qty / config.minQty) * config.minQty;
    }
    // Threshold or default mode
    return Math.max(config.minQty, qty);
  }

  /**
   * PDP: Override quantity adjuster behavior
   * SH Updated: January 2026 - Read config on each click for variant support
   */
  function initPDPQuantity() {
    const qtyAdjuster = document.querySelector('[data-qty-adjuster]');
    if (!qtyAdjuster) return;

    const input = qtyAdjuster.querySelector('[data-product-qty]');
    const btnMinus = qtyAdjuster.querySelector('.qty-adjuster-btn--less');
    const btnPlus = qtyAdjuster.querySelector('.qty-adjuster-btn--plus');

    if (!input) return;

    // Override minus button (capture phase to run before theme JS)
    // Read config fresh on each click to support variant changes
    btnMinus?.addEventListener('click', function (e) {
      const config = getQuantityConfig(qtyAdjuster);
      if (config.mode === 'default') return; // Let default theme behavior handle it
      e.stopImmediatePropagation();
      const currentQty = parseInt(input.value) || config.minQty;
      const newQty = Math.max(config.minQty, currentQty - config.increment);
      input.value = newQty;
    }, true);

    // Override plus button
    // Read config fresh on each click to support variant changes
    btnPlus?.addEventListener('click', function (e) {
      const config = getQuantityConfig(qtyAdjuster);
      if (config.mode === 'default') return; // Let default theme behavior handle it
      e.stopImmediatePropagation();
      const currentQty = parseInt(input.value) || config.minQty;
      const newQty = currentQty + config.increment;
      input.value = newQty;
    }, true);

    // Validate on blur (when user types a number)
    // Read config fresh to support variant changes
    input.addEventListener('blur', function () {
      const config = getQuantityConfig(qtyAdjuster);
      if (config.mode === 'default') return;
      const currentQty = parseInt(input.value) || config.minQty;
      input.value = getValidQuantity(currentQty, config);
    });

    // Validate on change
    // Read config fresh to support variant changes
    input.addEventListener('change', function () {
      const config = getQuantityConfig(qtyAdjuster);
      if (config.mode === 'default') return;
      const currentQty = parseInt(input.value) || config.minQty;
      input.value = getValidQuantity(currentQty, config);
    });
  }

  /**
   * Product Sets: Override quantity adjuster behavior
   * SH Updated: January 2026 - Read config fresh on each interaction for consistency
   */
  function initSetProductQuantity() {
    const qtyAdjusters = document.querySelectorAll('[data-set-product-qty-adjuster]');
    if (!qtyAdjusters.length) return;

    qtyAdjusters.forEach(function (qtyAdjuster) {
      const productId = qtyAdjuster.dataset.setProductQtyAdjuster;
      const input = qtyAdjuster.querySelector('[data-set-product-qty="' + productId + '"]');
      const btnMinus = qtyAdjuster.querySelector('.qty-adjuster-btn--less');
      const btnPlus = qtyAdjuster.querySelector('.qty-adjuster-btn--plus');

      if (!input) return;

      // Override minus button (capture phase to run before theme JS)
      // Read config fresh on each click for consistency
      btnMinus?.addEventListener('click', function (e) {
        const config = getQuantityConfig(qtyAdjuster);
        if (config.mode === 'default') return;
        e.stopImmediatePropagation();
        const currentQty = parseInt(input.value) || config.minQty;
        const newQty = Math.max(config.minQty, currentQty - config.increment);
        input.value = newQty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, true);

      // Override plus button
      // Read config fresh on each click for consistency
      btnPlus?.addEventListener('click', function (e) {
        const config = getQuantityConfig(qtyAdjuster);
        if (config.mode === 'default') return;
        e.stopImmediatePropagation();
        const currentQty = parseInt(input.value) || config.minQty;
        const newQty = currentQty + config.increment;
        input.value = newQty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, true);

      // Validate on blur (when user types a number)
      // Read config fresh for consistency
      input.addEventListener('blur', function () {
        const config = getQuantityConfig(qtyAdjuster);
        if (config.mode === 'default') return;
        const currentQty = parseInt(input.value) || config.minQty;
        input.value = getValidQuantity(currentQty, config);
      });

      // Validate on change
      // Read config fresh for consistency
      input.addEventListener('change', function () {
        const config = getQuantityConfig(qtyAdjuster);
        if (config.mode === 'default') return;
        const currentQty = parseInt(input.value) || config.minQty;
        input.value = getValidQuantity(currentQty, config);
      });
    });
  }

  /**
   * Cart: Override quantity controls
   */
  function initCartQuantity() {
    document.addEventListener('click', function (e) {
      const increaseBtn = e.target.closest('[data-cart-increase]');
      const decreaseBtn = e.target.closest('[data-cart-decrease]');

      if (!increaseBtn && !decreaseBtn) return;

      const cartItem = (increaseBtn || decreaseBtn).closest('[data-cart-item]');
      if (!cartItem) return;

      const config = getQuantityConfig(cartItem);
      if (config.mode === 'default') return;

      const qtyInput = cartItem.querySelector('[data-cart-quantity]');
      if (!qtyInput) return;

      e.stopImmediatePropagation();
      e.preventDefault();

      const currentQty = parseInt(qtyInput.value) || config.minQty;
      let newQty;

      if (increaseBtn) {
        newQty = currentQty + config.increment;
      } else {
        if (currentQty <= config.minQty) {
          // At minimum, trigger remove instead
          const removeBtn = cartItem.querySelector('[data-cart-remove]');
          if (removeBtn) removeBtn.click();
          return;
        }
        newQty = currentQty - config.increment;
      }

      qtyInput.value = newQty;
      updateCartQuantity(cartItem.dataset.variantId, newQty);
    }, true);
  }

  /**
   * Cart API update
   */
  function updateCartQuantity(variantId, quantity) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [variantId]: quantity } })
    })
      .then(r => r.json())
      .then(() => {
        document.dispatchEvent(new CustomEvent('cartUpdated'));
        location.reload();
      });
  }

  /**
   * Validate cart quantities on page load (round up invalid quantities)
   */
  function validateCartQuantities() {
    document.querySelectorAll('[data-cart-item]').forEach(item => {
      const config = getQuantityConfig(item);
      if (config.mode === 'default') return;

      const qtyInput = item.querySelector('[data-cart-quantity]');
      if (!qtyInput) return;

      const currentQty = parseInt(qtyInput.value) || config.minQty;
      const validQty = getValidQuantity(currentQty, config);

      if (currentQty !== validQty) {
        updateCartQuantity(item.dataset.variantId, validQty);
      }
    });
  }

  /**
   * Get min qty config for a specific variant from global product JSON
   * Priority: variant metafield > product metafield > default (1)
   */
  function getVariantMinQtyConfig(variantId) {
    const product = window.eHS?.product;
    if (!product) return { minQty: 1, increment: 1, mode: 'default' };

    const variant = product.variants.find(v => v.id === variantId || v.id === parseInt(variantId));

    const variantMinQty = variant?.minPurchaseQty || 0;
    const variantMinThreshold = variant?.minThreshold || 0;
    const productMinQty = product.minPurchaseQty || 0;
    const productMinThreshold = product.minThreshold || 0;

    const effectiveMinQty = variantMinQty > 0 ? variantMinQty : productMinQty;
    const effectiveMinThreshold = variantMinThreshold > 0 ? variantMinThreshold : productMinThreshold;

    if (effectiveMinQty > 1) {
      return { minQty: effectiveMinQty, increment: effectiveMinQty, mode: 'increment' };
    }
    if (effectiveMinThreshold > 1) {
      return { minQty: effectiveMinThreshold, increment: 1, mode: 'threshold' };
    }
    return { minQty: 1, increment: 1, mode: 'default' };
  }

  /**
   * Update quantity adjuster when variant changes
   * SH Updated: January 2026 - Reset to variant's minimum on variant change
   */
  function updateQuantityAdjuster(variantId) {
    const qtyAdjuster = document.querySelector('[data-qty-adjuster]');
    if (!qtyAdjuster) return;

    const config = getVariantMinQtyConfig(variantId);
    const input = qtyAdjuster.querySelector('[data-product-qty]');
    if (!input) return;

    // Update data attributes
    delete qtyAdjuster.dataset.minQty;
    delete qtyAdjuster.dataset.minThreshold;

    if (config.mode === 'increment') {
      qtyAdjuster.dataset.minQty = config.minQty;
    } else if (config.mode === 'threshold') {
      qtyAdjuster.dataset.minThreshold = config.minQty;
    }

    // Reset input to variant's minimum quantity
    input.value = config.minQty;
    input.min = config.minQty;
  }

  /**
   * Get currently selected variant ID
   */
  function getCurrentSelectedVariantId() {
    const product = window.eHS?.product;
    if (!product?.variants) return null;

    // Check for selected variant in URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) return parseInt(variantParam);

    // Get selected options from DOM (button-style selectors)
    const selectedBtns = document.querySelectorAll('[data-product-option-selector].active, [data-product-option-selector][aria-checked="true"]');
    if (selectedBtns.length === 0) return product.defaultVariant;

    const selectedOptions = Array.from(selectedBtns).map(btn => btn.dataset.productOption || btn.textContent.trim());

    const variant = product.variants.find(v =>
      v.options.every((opt, idx) => opt === selectedOptions[idx])
    );

    return variant?.id || product.defaultVariant;
  }

  /**
   * Listen for variant changes and update quantity adjuster
   */
  function initVariantChangeListener() {
    // Listen for variant option button clicks
    document.addEventListener('click', function (e) {
      const optionBtn = e.target.closest('[data-product-option-selector]');
      if (!optionBtn) return;

      // Debounce - variant change happens after click is processed
      setTimeout(function () {
        const variantId = getCurrentSelectedVariantId();
        if (variantId) updateQuantityAdjuster(variantId);
      }, 50);
    });

    // Listen for dropdown changes (for dropdown-style variant selectors)
    document.addEventListener('change', function (e) {
      if (e.target.matches('[data-option-group]')) {
        setTimeout(function () {
          const variantId = getCurrentSelectedVariantId();
          if (variantId) updateQuantityAdjuster(variantId);
        }, 50);
      }
    });
  }

  /**
   * Initialize
   */
  function init() {
    initPDPQuantity();
    initSetProductQuantity();
    initCartQuantity();
    validateCartQuantities();
    initVariantChangeListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
